"use client";

import type React from "react";
import { observer } from "mobx-react-lite";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Users,
  BarChart3,
  Star,
  Crown,
  Heart,
  Briefcase,
  AlertCircle,
  CheckCircle2,
  Flag,
  Activity,
  TrendingUp,
  Database,
  Filter,
  RefreshCw,
} from "lucide-react";
import { useAppContext } from "../../../shared/functions/Context";
import type { IValueRating } from "../../../shared/models/three-sixty-feedback-models/ValueRating";
import type { ITemplateRating } from "../../../shared/models/three-sixty-feedback-models/TemplateRating";
import ModernRatingsTable from "../ratings/components/ModernRatingsTable";

interface ILeadershipRatings {
  overallRating: number;
  id: string;
  rateeId: string;
  dimension: string;
  isflagged: boolean;
  department: string;
  description: string;
  criteria: {
    [raterId: string]: {
      additionalComment?: string;
      stopDoing: string;
      startDoing: string;
      continueDoing: string;
      ratings: {
        [criteriaName: string]: {
          [statement: string]: number;
        };
      };
      leadershipScore?: number;
    };
  };
  date: number;
  raterId?: string;
  templateId?: string;
  heading?: string;
  reasonForRequest?: string;
  dueDate?: number;
  values?: {};
  archived?: boolean;
}

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  gradient: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  gradient,
  trend,
  loading,
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200/50 animate-pulse">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-slate-200 rounded-xl"></div>
          <div className="flex-1">
            <div className="h-5 bg-slate-200 rounded mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-2/3"></div>
          </div>
        </div>
        <div className="h-8 bg-slate-200 rounded mb-2"></div>
        <div className="h-4 bg-slate-200 rounded w-1/2"></div>
      </div>
    );
  }

  return (
    <div className="group bg-white rounded-2xl p-6 shadow-lg border border-slate-200/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="flex items-center gap-4 mb-4">
        <div
          className={`p-3 bg-gradient-to-br ${gradient} rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300`}
        >
          <div className="text-white">{icon}</div>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
            {title}
          </h3>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div className="text-3xl font-bold text-slate-800">{value}</div>
        {trend && (
          <div
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              trend.isPositive
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            <TrendingUp
              className={`h-3 w-3 ${
                trend.isPositive ? "rotate-0" : "rotate-180"
              }`}
            />
            {Math.abs(trend.value)}%
          </div>
        )}
      </div>
    </div>
  );
};

const AdminAllRatings = observer(() => {
  const { api, store } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [selectedDescription, setSelectedDescription] = useState<string>("all");
  const [refreshing, setRefreshing] = useState(false);
  const [description, setDescription] = useState("");

  const allUsers = store.user.all.map((u) => u.asJson.uid);

  // Get all available descriptions from rating assignments
  const availableDescriptions = useMemo(() => {
    const descriptions = store.ratingAssignments.all
      .map((assignment) => assignment.asJson.description)
      .filter(Boolean);
    return Array.from(new Set(descriptions));
  }, [store.ratingAssignments.all]);

  // Mapping functions to convert different rating types to common format
  const mapValueToLeadership = useCallback(
    (valueRating: IValueRating): ILeadershipRatings => {
      const mappedLeadershipRating: ILeadershipRatings = {
        id: valueRating.id,
        rateeId: valueRating.rateeId,
        date: valueRating.date,
        dimension: valueRating.dimension,
        criteria: {},
        overallRating: valueRating.overallRating,
        isflagged: valueRating.isflagged,
        department: valueRating.department,
        description: valueRating.description,
      };

      Object.keys(valueRating.values).forEach((raterId) => {
        const criteriaName = Object.keys(
          valueRating.values[raterId].ratings
        )[0];
        mappedLeadershipRating.criteria[raterId] = {
          additionalComment: valueRating.values[raterId].additionalComment,
          stopDoing: valueRating.values[raterId].stopDoing,
          startDoing: valueRating.values[raterId].startDoing,
          continueDoing: valueRating.values[raterId].continueDoing,
          ratings: {
            [criteriaName]: valueRating.values[raterId].ratings[criteriaName],
          },
        };
      });

      return mappedLeadershipRating;
    },
    []
  );

  const mapTemplateToLeadership = useCallback(
    (templateRating: ITemplateRating): ILeadershipRatings => {
      const mappedLeadershipRating: ILeadershipRatings = {
        id: templateRating.id,
        rateeId: templateRating.rateeId,
        date: templateRating.dateRequested ?? 0,
        dimension: templateRating.templateId,
        criteria: {},
        overallRating: 0,
        isflagged: templateRating.isflagged,
        department: templateRating.department,
        description: templateRating.description,
        raterId: templateRating.raterId,
        templateId: templateRating.templateId,
        heading: templateRating.heading,
        reasonForRequest: templateRating.reasonForRequest,
        dueDate: templateRating.dueDate,
        values: templateRating.values,
      };

      Object.keys(templateRating.values).forEach((raterId) => {
        const criteriaName = Object.keys(
          templateRating.values[raterId].ratings
        )[0];
        mappedLeadershipRating.criteria[raterId] = {
          additionalComment: templateRating.values[raterId].additionalComment,
          stopDoing: templateRating.values[raterId].stopDoing,
          startDoing: templateRating.values[raterId].startDoing,
          continueDoing: templateRating.values[raterId].continueDoing,
          ratings: {
            [criteriaName]:
              templateRating.values[raterId].ratings[criteriaName],
          },
        };
      });

      return mappedLeadershipRating;
    },
    []
  );

  // Get all ratings and filter by description if selected
  const allRatings = useMemo(() => {
    const valueRatings = store.valueRating.all
      .map((rating) => rating.asJson)
      .filter(
        (rating) =>
          selectedDescription === "all" ||
          rating.description === selectedDescription
      )
      .filter(
        (rating) =>
          rating.adminApproval !== "Approved for Re-rating" &&
          rating.resubmission !== "To be rated" &&
          rating.resubmission !== "Closed"
      );

    const leadershipRatings = store.leadershipRating.all
      .map((rating) => rating.asJson)
      .filter(
        (rating) =>
          selectedDescription === "all" ||
          rating.description === selectedDescription
      )
      .filter(
        (rating) =>
          rating.adminApproval !== "Approved for Re-rating" &&
          rating.resubmission !== "To be rated" &&
          rating.resubmission !== "Closed"
      );

    const templateRatings = store.templateRating.all
      .map((rating) => rating.asJson)
      .filter(
        (rating) =>
          selectedDescription === "all" ||
          rating.description === selectedDescription
      )
      // .filter((rating) => rating.status === "Completed");

  

    const valueRates = valueRatings.map(mapValueToLeadership);
    const templateRates = templateRatings.map(mapTemplateToLeadership);

    return [...valueRates, ...leadershipRatings, ...templateRates];
  }, [
    store.valueRating.all,
    store.leadershipRating.all,
    store.templateRating.all,
    selectedDescription,
    mapValueToLeadership,
    mapTemplateToLeadership,
  ]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalRatings = allRatings.length;
    const flaggedRatings = allRatings.filter(
      (rating) => rating.isflagged
    ).length;
    const completedRatings = allRatings.filter(
      (rating) => !rating.isflagged
    ).length;
    const valueRatings = allRatings.filter(
      (rating) => rating.dimension === "Values"
    ).length;
    const leadershipRatings = allRatings.filter(
      (rating) => rating.dimension === "Leadership"
    ).length;
    const templateRatings = allRatings.filter(
      (rating) =>
        rating.dimension !== "Values" && rating.dimension !== "Leadership"
    ).length;

    // Calculate unique ratees and raters
    const uniqueRatees = new Set(allRatings.map((rating) => rating.rateeId))
      .size;
    const uniqueRaters = new Set(
      allRatings.flatMap((rating) => Object.keys(rating.criteria))
    ).size;

    return {
      totalRatings,
      flaggedRatings,
      completedRatings,
      valueRatings,
      leadershipRatings,
      templateRatings,
      uniqueRatees,
      uniqueRaters,
      flaggedPercentage:
        totalRatings > 0
          ? ((flaggedRatings / totalRatings) * 100).toFixed(1)
          : "0",
    };
  }, [allRatings]);

  // Create a reusable load function
  const loadData = useCallback(
    async (targetDescription?: string) => {
      try {
        setLoading(true);

        // 1. Load rating assignments and users first
        await api.ratingAssignments.getAll();
        await api.user.getAll();

        const activeRating = store.ratingAssignments.all.find(
          (rating) => rating.asJson.isActive === true
        )?.asJson;

        let descriptionToUse = targetDescription;

        // If no target description provided, use active rating description
        if (!descriptionToUse && activeRating) {
          descriptionToUse = activeRating.description ?? "";
          setDescription(descriptionToUse);
        }

        // 2. Load ratings based on description
        if (allUsers.length > 0 && descriptionToUse) {
         
          await Promise.all([
            api.valueRating.getAllNew(allUsers, descriptionToUse),
            api.leadershipRating.getAllNew(allUsers, descriptionToUse),
            api.templateRating.getAllWithDescription(descriptionToUse),
          ]);
        } else {
          console.warn("No valid users or description found, loading all data");
          // Fallback to load all data
          await Promise.all([api]);
        }
      } catch (error) {
        console.error("Error occurred while loading data:", error);
      }
       setLoading(false)
    },
    [api]
  );

  // Initial load
  useEffect(() => {
    const initialLoad = async () => {
      setLoading(true)
      try {
        await loadData()
      } finally {
        setLoading(false)
      }
    }

    initialLoad()
  }, [loadData])

  // Update the description filter handler to refetch data when changed
  const handleDescriptionChange = useCallback(
    async (newDescription: string) => {
      setSelectedDescription(newDescription);

      if (newDescription !== "all") {
        try {
          setRefreshing(true);
          console.log(
            "Fetching ratings for selected description:",
            newDescription
          );

          // Use the same API calls as in loadData
          if (allUsers.length > 0) {
            await Promise.all([
              api.valueRating.getAllNew(allUsers, newDescription),
              api.leadershipRating.getAllNew(allUsers, newDescription),
              api.templateRating.getAllWithDescription(newDescription),
            ]);
          } else {
            // Fallback API calls
            await Promise.all([
              api.valueRating.getAllByDescription(newDescription),
              //   api.leadershipRating.getAllLeadershipByDescription(newDescription),
              api.templateRating.getAllWithDescription(newDescription),
            ]);
          }
        } catch (error) {
          console.error("Error fetching data for description:", error);
        } finally {
          setRefreshing(false);
        }
      } else {
        // If "all" is selected, reload all data
        try {
          setRefreshing(true);
          await Promise.all([api]);
        } catch (error) {
          console.error("Error fetching all data:", error);
        } finally {
          setRefreshing(false);
        }
      }
    },
    [api, allUsers]
  );

  // Fixed refresh function
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (selectedDescription === "all") {
        // Refresh all data
        await Promise.all([
          api.valueRating.getAllNew(allUsers, description),
          api.leadershipRating.getAllNew(allUsers, description),
          api.templateRating.getAllWithDescription(description),
        ]);
      } else {
        // Refresh data for selected description
        await loadData(selectedDescription);
      }
      console.log("Data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setRefreshing(false);
    }
  }, [api]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Database className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              All Ratings Management
            </h2>
            <p className="text-slate-600">
              Comprehensive view of all system ratings
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Description Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={selectedDescription}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              disabled={refreshing}
            >
              <option value="all">All Sessions</option>
              {availableDescriptions.map((desc) => (
                <option key={desc} value={desc}>
                  {desc}
                </option>
              ))}
            </select>
          </div>
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Ratings"
          value={statistics.totalRatings}
          subtitle="All completed ratings"
          icon={<BarChart3 className="h-6 w-6" />}
          gradient="from-blue-500 to-indigo-600"
          loading={loading}
        />
        <StatCard
          title="Flagged Ratings"
          value={statistics.flaggedRatings}
          subtitle={`${statistics.flaggedPercentage}% of total`}
          icon={<Flag className="h-6 w-6" />}
          gradient="from-red-500 to-rose-600"
          trend={{
            value: Number.parseFloat(statistics.flaggedPercentage),
            isPositive: false,
          }}
          loading={loading}
        />
        <StatCard
          title="Active Users"
          value={statistics.uniqueRatees}
          subtitle="Users being rated"
          icon={<Users className="h-6 w-6" />}
          gradient="from-green-500 to-emerald-600"
          loading={loading}
        />
        <StatCard
          title="Total Raters"
          value={statistics.uniqueRaters}
          subtitle="Users providing ratings"
          icon={<Activity className="h-6 w-6" />}
          gradient="from-purple-500 to-violet-600"
          loading={loading}
        />
      </div>

      {/* Rating Type Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Values Ratings"
          value={statistics.valueRatings}
          subtitle="Core organizational values"
          icon={<Heart className="h-6 w-6" />}
          gradient="from-blue-500 to-cyan-600"
          loading={loading}
        />
        <StatCard
          title="Leadership Ratings"
          value={statistics.leadershipRatings}
          subtitle="Leadership competencies"
          icon={<Crown className="h-6 w-6" />}
          gradient="from-purple-500 to-pink-600"
          loading={loading}
        />
        <StatCard
          title="Template Ratings"
          value={statistics.templateRatings}
          subtitle="Custom assessments"
          icon={<Briefcase className="h-6 w-6" />}
          gradient="from-emerald-500 to-teal-600"
          loading={loading}
        />
      </div>

      {/* Status Summary */}
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
            <Star className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">
              System Overview
            </h3>
            <p className="text-slate-600">
              Current status of all ratings in the system
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-800">
              {statistics.completedRatings}
            </div>
            <div className="text-sm text-slate-600">Completed</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center">
            <AlertCircle className="h-8 w-8 text-amber-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-800">
              {statistics.flaggedRatings}
            </div>
            <div className="text-sm text-slate-600">Flagged</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center">
            <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-800">
              {statistics.uniqueRatees}
            </div>
            <div className="text-sm text-slate-600">Ratees</div>
          </div>
          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center">
            <Activity className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-800">
              {statistics.uniqueRaters}
            </div>
            <div className="text-sm text-slate-600">Raters</div>
          </div>
        </div>
      </div>

      {/* All Ratings Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Database className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">
                  All System Ratings
                </h3>
                <p className="text-slate-600">
                  {selectedDescription === "all"
                    ? "Complete list of all ratings across all sessions"
                    : `Ratings for session: ${selectedDescription}`}
                </p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              {statistics.totalRatings} total ratings
            </div>
          </div>
        </div>
        <ModernRatingsTable
          data={allRatings}
          description={selectedDescription === "all" ? "" : selectedDescription}
        />
      </div>
    </div>
  );
});

export default AdminAllRatings;
